import React from 'react';
import { StyleSheet, Text, View, Platform, useWindowDimensions } from 'react-native';
import MaterialIcons from 'react-native-vector-icons/MaterialIcons';
import { WARNING_RED } from '../../../constants/colors';
import { FontFamily } from '../../../../../constants/fonts';
import KpiInfoTooltip from './KpiInfoTooltip';

interface MetricCardProps {
  title: string;
  value: string;
  description?: string;
  valueColor?: string;
  trendText?: string;
  trendDirection?: 'up' | 'down';
  trendImpact?: 'positive' | 'negative' | 'neutral' | null;
  icon?: string;
  iconColor?: string;
  /** Hover tooltip describing the metric's definition/calculation/target (mockup copy). */
  infoTooltip?: string;
}

const getTrendColor = (trendImpact?: MetricCardProps['trendImpact']): string => {
  if (trendImpact === 'positive') return '#0B5FC6';
  if (trendImpact === 'negative') return WARNING_RED;
  return '#4B5563';
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  description,
  valueColor,
  trendText,
  trendDirection,
  trendImpact,
  icon,
  iconColor,
  infoTooltip,
}) => {
  const { width } = useWindowDimensions();
  const isMobileIos = Platform.OS === 'ios' && width < 768;
  const trendPrefix = trendDirection === 'up' ? '▲ ' : trendDirection === 'down' ? '▼ ' : '';

  return (
    <View style={[styles.card, isMobileIos && styles.cardMobileIos]}>
      <View style={styles.headerRow}>
        <View style={styles.headerLeft}>
          {icon ? (
            <MaterialIcons name={icon as any} size={16} color={iconColor ?? '#0B5FC6'} style={styles.icon} />
          ) : null}
          <Text style={styles.title} numberOfLines={1}>{title}</Text>
        </View>
        {infoTooltip ? <KpiInfoTooltip text={infoTooltip} /> : <MaterialIcons name="info-outline" size={14} color="#ADB5BD" />}
      </View>
      <Text style={[styles.value, valueColor ? { color: valueColor } : null]}>{value}</Text>
      {description ? <Text style={styles.description}>{description}</Text> : null}
      {trendText ? (
        <Text style={[styles.trend, { color: getTrendColor(trendImpact) }]}>
          {trendPrefix}
          {trendText}
        </Text>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    paddingHorizontal: 18,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#E3E9F3',
    shadowColor: '#A8B4C5',
    shadowOpacity: 0.1,
    shadowRadius: 6,
    shadowOffset: { width: 0, height: 2 },
    elevation: 1,
    height: 124,
  },
  cardMobileIos: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    flexShrink: 1,
    gap: 6,
  },
  icon: {
    marginRight: 2,
  },
  title: {
    fontSize: 12,
    fontFamily: FontFamily.bold,
    fontWeight: '600',
    letterSpacing: 0.4,
    color: '#6B7280',
    textTransform: 'uppercase',
  },
  value: {
    fontSize: 30,
    fontFamily: FontFamily.bold,
    fontWeight: '700',
    color: '#111827',
  },
  description: {
    marginTop: 4,
    fontSize: 12,
    fontFamily: FontFamily.regular,
    color: '#6B7280',
  },
  trend: {
    marginTop: 8,
    fontSize: 12,
    fontFamily: FontFamily.medium,
    fontWeight: '600',
  },
});

export default MetricCard;
